/* ============================================================================
   hej-journal.js — interactie voor de vereenvoudigde blogpagina en de
   tag-overzichten. Geladen als type="module" door de hej-journal-*.liquid
   secties: meerdere script-tags met dezelfde src draaien daardoor
   gegarandeerd maar één keer.

   Onderdelen:
   1. Reveal: rustige eenmalige fade + translateY via IntersectionObserver.
      Zonder JS, met prefers-reduced-motion of in de theme-editor is alle
      content gewoon direct zichtbaar (zelfde vangnet-principe als
      assets/hej-reveal.js).
   2. Laad meer (tag-overzicht): volgende pagina ophalen via de Shopify
      Section Rendering API en de kaarten onder het grid toevoegen, zonder
      scrollsprong en zonder dubbele artikelen. Bij een fout valt de knop
      terug op gewone navigatie naar de volgende pagina.
   Alles is idempotent en herbindt netjes bij Shopify theme-editor events.
   ========================================================================= */

(function () {
  'use strict';

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { REDUCED = true; }

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  /* ── 1. Reveal ─────────────────────────────────────────────────────────── */

  function initReveal(root) {
    if (DESIGN_MODE || REDUCED || !('IntersectionObserver' in window)) return;

    var doelen = [];
    function voegToe(el) {
      if (el.dataset.hejJnRevealKlaar || doelen.indexOf(el) !== -1) return;
      doelen.push(el);
    }
    root.querySelectorAll('[data-hej-jn-reveal]').forEach(voegToe);
    root.querySelectorAll('[data-hej-jn-reveal-groep]').forEach(function (groep) {
      Array.prototype.forEach.call(groep.children, voegToe);
    });
    if (!doelen.length) return;

    doelen.forEach(function (el) {
      el.dataset.hejJnRevealKlaar = '1';
      el.classList.add('hej-jn-reveal-init');
    });

    var io = new IntersectionObserver(function (entries) {
      var zichtbaar = entries.filter(function (e) { return e.isIntersecting; });
      zichtbaar.forEach(function (entry, i) {
        var el = entry.target;
        io.unobserve(el);
        var vertraging = Math.min(i * 90, 320);
        el.style.transitionDelay = vertraging ? vertraging + 'ms' : '';
        /* volgende frame, zodat de begintoestand eerst geschilderd is */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.classList.add('hej-jn-reveal-in');
          });
        });
        var klaar = false;
        function opruimen() {
          if (klaar) return;
          klaar = true;
          el.style.transitionDelay = '';
          el.classList.remove('hej-jn-reveal-init', 'hej-jn-reveal-in');
        }
        el.addEventListener('transitionend', function h(e) {
          if (e.target !== el || e.propertyName !== 'opacity') return;
          el.removeEventListener('transitionend', h);
          opruimen();
        });
        setTimeout(opruimen, vertraging + 1100); /* vangnet */
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });

    doelen.forEach(function (el) { io.observe(el); });
  }

  /* ── 2. Laad meer (tag-overzicht) ──────────────────────────────────────── */

  var bezigMetLaden = false;

  function laadVolgendePagina(knop) {
    var lijst = document.querySelector('[data-hej-jn-grid]');
    if (!knop || !lijst || bezigMetLaden) return;

    var volgende = knop.getAttribute('href');
    var sectieId = lijst.getAttribute('data-hej-jn-sectie');
    if (!volgende || !sectieId) return;

    bezigMetLaden = true;
    knop.classList.add('is-laden');
    knop.setAttribute('aria-busy', 'true');

    var scheider = volgende.indexOf('?') === -1 ? '?' : '&';
    var url = volgende + scheider + 'section_id=' + encodeURIComponent(sectieId);

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var bestaand = {};
        lijst.querySelectorAll('[data-hej-jn-item]').forEach(function (item) {
          bestaand[item.getAttribute('data-hej-jn-url')] = true;
        });

        doc.querySelectorAll('[data-hej-jn-item]').forEach(function (nieuw) {
          var artikelUrl = nieuw.getAttribute('data-hej-jn-url');
          if (bestaand[artikelUrl]) return; /* nooit dubbele artikelen */
          lijst.appendChild(document.importNode(nieuw, true));
        });

        var nieuweKnop = doc.querySelector('[data-hej-jn-meer]');
        if (nieuweKnop && nieuweKnop.getAttribute('href')) {
          knop.setAttribute('href', nieuweKnop.getAttribute('href'));
        } else {
          /* geen volgende pagina meer: knop en wrap definitief weghalen */
          var wrap = document.querySelector('[data-hej-jn-meer-wrap]');
          if (wrap) wrap.hidden = true;
          knop.remove();
        }
      })
      .catch(function () {
        /* netwerkfout: val terug op gewone navigatie naar de volgende pagina */
        window.location.href = volgende;
      })
      .finally(function () {
        bezigMetLaden = false;
        knop.classList.remove('is-laden');
        knop.removeAttribute('aria-busy');
      });
  }

  /* ── 3. Hero-peek-nudge ───────────────────────────────────────────────── */

  /* Herhaalde "kijk-ik-ben-hier"-nudge op de grijze band direct na de hero
     (verzoek gebruiker 2026-08-05): elke nudge komt sneller dan de vorige,
     zolang de bezoeker niet scrolt. Zodra er gescrold wordt, pauzeert het
     ritme; helemaal terug bovenaan (scrollY ~0) start het weer vanaf het
     begintempo. De beweging zelf staat in assets/hej-journal.css
     (.hej-jn-peek-nudge); hier alleen het ritme ertussen, want
     CSS-animaties kunnen zichzelf niet laten versnellen over herhalingen
     heen.

     Twee extra voorwaarden (verzoek gebruiker 2026-08-05):
     1. Alleen op schermen waar het randje NIET al vanzelf opvalt: op een
        ruime monitor is er vaak al 200-300px grijs zichtbaar (geen nudge
        nodig), op een kleine 14"-laptop soms maar het gegarandeerde
        minimum. PEEK_THRESHOLD bepaalt die grens. Deze wordt live gemeten
        (ook bij resize, bugfix 2026-08-06): een venster dat tussen een
        monitor en een 14"-scherm heen en weer wordt gesleept wisselt zo
        direct mee, in plaats van vast te zitten aan de meting bij het
        laden.
     2. Gepauzeerd zolang er een overlay open staat (menu/zoeken/winkelmand
        — allemaal herkenbaar aan body.lvh-noscroll, zie sections/header.
        liquid), zodat er niks animeert achter de menu-blur. */
  function initHeroPeekNudge(root) {
    if (DESIGN_MODE || REDUCED) return;
    var band = root.querySelector('.shopify-section--hej-journal-hero + .shopify-section .hej-jn-ov');
    if (!band || band.dataset.hejJnNudgeInit) return;
    band.dataset.hejJnNudgeInit = '1';

    var PEEK_THRESHOLD = 120; /* px: boven deze grens is het randje al vanzelf duidelijk zichtbaar */
    var START_DELAY = 2000;
    var MIN_DELAY = 1400; /* moet >= animatieduur (1.3s) blijven, anders botst de volgende nudge met het einde van de vorige */
    var delay = START_DELAY;
    var timerId = null;
    var resizeTimerId = null;
    var actief = false;
    var gescrold = false;
    var overlayOpen = document.body.classList.contains('lvh-noscroll');

    function teSmalVoorNudge() {
      var peekPx = window.innerHeight - band.getBoundingClientRect().top;
      return peekPx <= PEEK_THRESHOLD;
    }

    function nudge() {
      if (!actief) return;
      band.classList.add('hej-jn-peek-nudge');
      band.addEventListener('animationend', function h() {
        band.removeEventListener('animationend', h);
        band.classList.remove('hej-jn-peek-nudge');
      });
      delay = Math.max(MIN_DELAY, Math.round(delay * 0.8));
      timerId = window.setTimeout(nudge, delay);
    }

    function start() {
      if (actief) return;
      actief = true;
      delay = START_DELAY;
      timerId = window.setTimeout(nudge, delay);
    }

    function pause() {
      if (!actief) return;
      actief = false;
      if (timerId) window.clearTimeout(timerId);
      band.classList.remove('hej-jn-peek-nudge');
    }

    function evaluate() {
      if (teSmalVoorNudge() && !gescrold && !overlayOpen) start(); else pause();
    }

    window.addEventListener('scroll', function () {
      gescrold = window.scrollY > 2;
      evaluate();
    }, { passive: true });

    window.addEventListener('resize', function () {
      if (resizeTimerId) window.clearTimeout(resizeTimerId);
      resizeTimerId = window.setTimeout(evaluate, 150);
    });

    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        overlayOpen = document.body.classList.contains('lvh-noscroll');
        evaluate();
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    evaluate();
  }

  /* ── init ──────────────────────────────────────────────────────────────── */

  function init(root) {
    var knop = root.querySelector('[data-hej-jn-meer]');
    if (knop && !knop.dataset.hejJnInit) {
      knop.dataset.hejJnInit = '1';
      knop.addEventListener('click', function (e) {
        e.preventDefault();
        laadVolgendePagina(knop);
      });
    }

    initReveal(root);
    initHeroPeekNudge(root);
  }

  function start() {
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* theme-editor: secties worden los herladen; opnieuw binden binnen de sectie */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
